import { TeContainer, TeContainerSlim } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { PlaceholderContainer, useNotification } from '@topcon/topcon-utils';
import React, { useState, useMemo } from 'react';

import { Section } from '../../utils/styling';
import RenderNotifications from './RenderNotifications';
import EmptyNotifications from './EmptyNotifications';
import NotificationsHeader from './NotificationsHeader';
import GenericPagination from '../../components/common/GenericPagination';
import { useNotifications, useUnreadNotificationsCount } from './notifications.queries';
import { useMarkAllAsRead } from './notifications.mutations';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';
import useDebounce from '../../hooks/useDebounce';

function Notifications() {
  const { t } = useTranslation('notifications');
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'unread'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // The input updates on every keystroke; the list query only sees the term
  // once typing pauses, so "acc" sends one request instead of one per letter.
  const debouncedSearchValue = useDebounce(searchValue);

  const filters = useMemo(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchValue || undefined,
      status: selectedFilter === 'unread' ? { read: false } : undefined,
    }),
    [currentPage, itemsPerPage, debouncedSearchValue, selectedFilter]
  );

  const { data: notifications, isLoading, isError } = useNotifications(filters);
  const notificationsCount = notifications?.total || 0;
  const noContent = isLoading || !notificationsCount;

  const { data: unreadCount = 0, isSuccess: isUnreadCountSuccess } = useUnreadNotificationsCount();

  const { mutateAsync: markAllAsRead, isPending: isMarkingAllAsRead } = useMarkAllAsRead();
  const { showError } = useErrorDrawer();
  const notificationService = useNotification();

  const markAllAsReadDisabled = isMarkingAllAsRead || (isUnreadCountSuccess && unreadCount === 0);

  const handleMarkAllAsRead = async () => {
    if (isMarkingAllAsRead) return;

    try {
      await markAllAsRead();
    } catch (error: unknown) {
      if (!showError(error)) {
        notificationService.addNotification(
          t('markAllAsReadFailedTitle'),
          t('markAllAsReadFailedDescription'),
          'error'
        );
      }
    }
  };

  const handleUnreadFilter = (value: 'all' | 'unread') => {
    setSelectedFilter(value);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const renderLoader = () => {
    return <PlaceholderContainer hasLoader text={t('loadingNotifications')} />;
  };

  const renderNoNotifications = () => {
    return <EmptyNotifications />;
  };

  const renderListError = () => {
    return <PlaceholderContainer icon="warning" text={t('listError')} />;
  };

  const renderNoContent = () => {
    return (
      <TeContainerSlim
        backgroundColor="background.secondary"
        borderRadius={4}
        fill
        justifyContent="center"
        alignItems="center"
      >
        {isError ? (
          renderListError()
        ) : isLoading ? (
          renderLoader()
        ) : !notificationsCount ? (
          renderNoNotifications()
        ) : (
          <PlaceholderContainer text={t('noSearchResults')} />
        )}
      </TeContainerSlim>
    );
  };

  return (
    <TeContainerSlim column grow padding="xs" overflow="auto" alignItems="center">
      <Section>
        <TeContainer
          justifyContent="center"
          backgroundColor="background.tertiary"
          overflow="hidden"
          padding={0}
          grow
        >
          <TeContainerSlim column grow gap="xs" relative>
            <NotificationsHeader
              title={t('notifications')}
              visibleItemsCount={selectedFilter === 'unread' ? unreadCount : notificationsCount}
              searchValue={searchValue}
              setSearchValue={handleSearchChange}
              searchPlaceholder={t('searchPlaceholder')}
              onMarkAllAsRead={handleMarkAllAsRead}
              markAllAsReadLoading={isMarkingAllAsRead}
              markAllAsReadDisabled={markAllAsReadDisabled}
              onUnreadFilter={handleUnreadFilter}
              showUnreadFilter={true}
              unreadCount={unreadCount}
              selectedFilter={selectedFilter}
            />
            <TeContainer backgroundColor="background.primary" borderRadius={4} fill column>
              {noContent ? (
                renderNoContent()
              ) : (
                <TeContainer column gap="s" fill>
                  <RenderNotifications notifications={notifications?.items || []} />

                  <GenericPagination
                    data={{
                      page: currentPage,
                      limit: itemsPerPage,
                      total: notifications?.total || 0,
                      items: notifications?.items || [],
                    }}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </TeContainer>
              )}
            </TeContainer>
          </TeContainerSlim>
        </TeContainer>
      </Section>
    </TeContainerSlim>
  );
}

export default React.memo(Notifications);
